import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Calendar, Briefcase, Building } from 'lucide-react';
import { Button } from './ui/button';
import techHubLogo from '../images/LOGO_TECH_HUB(1).png';
import facultadLogo from '../images/logo_upe_infor_1(1).png';
import upeLogo from '../images/icon_upe-300x300.png';

export default function Header({ user, logout }) {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const userMenuRef = useRef(null);

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleSavedClick = () => {
    navigate('/saved');
  };

  const handleChangePasswordClick = () => {
    setShowUserMenu(false);
    navigate('/change-password');
  };

  const handleLoginClick = () => {
    navigate('/');
  };

  // Close menu when clicking outside
  useEffect(() => {
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showUserMenu]);

  return (
    <>
      {/* Top bar with logos */}
      <div className="bg-slate-800 border-b border-slate-700 th-header-top-fixed">
        <div className="max-w-7xl mx-auto grid grid-cols-[1fr_auto_1fr] items-center gap-4 sm:gap-6 md:gap-8 px-2 sm:px-4">
          {/* Logo izquierdo - UPE */}
          <div className="flex items-center justify-center">
            <img src={upeLogo} alt="Universidad Privada del Este" className="logo-img logo-left" />
          </div>

          {/* Logo centro - TechHub */}
          <div className="flex items-center justify-center">
            <img src={techHubLogo} alt="TechHub UPE" className="logo-img logo-center" />
          </div>

          {/* Logo derecho - Facultad de Informática */}
          <div className="flex items-center justify-center">
            <img src={facultadLogo} alt="Facultad de Ciencias de la Informática" className="logo-img logo-right" />
          </div>
        </div>
      </div>

      {/* Navigation menu */}
  <header className="bg-slate-900 border-b border-cyan-500/20 px-4 py-3 sticky top-0 z-50 th-header">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <nav className="flex-1">
            {/* Mobile: show hamburger to toggle nav; Desktop: always show centered nav */}
            <div className="flex items-center">
              {/* Hamburger for mobile */}
              <button
                aria-label="Toggle navigation"
                onClick={() => setMobileOpen(v => !v)}
                className="mr-3 md:hidden p-2 rounded bg-slate-800 border border-slate-700 text-gray-200"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" /></svg>
              </button>
              {/* horizontal nav: only visible on md+; on mobile we use the dropdown */}
              <div id="main-nav" className={`hidden md:flex w-full items-center justify-center gap-3 md:gap-6 overflow-x-auto`}>
                <Button
                  key="inicio-btn"
                  variant="ghost"
                  onClick={() => navigate('/dashboard')}
                  className="text-gray-300 hover:text-cyan-400 px-3 md:px-4"
                >
                  Inicio
                </Button>
                <Button
                  key="cursos-btn"
                  variant="ghost"
                  onClick={() => navigate('/courses')}
                  className="text-gray-300 hover:text-cyan-400 px-3 md:px-4"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  <span className="whitespace-nowrap">Cursos</span>
                </Button>
                <Button
                  key="eventos-btn"
                  variant="ghost"
                  onClick={() => navigate('/events')}
                  className="text-gray-300 hover:text-cyan-400 px-3 md:px-4"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  <span className="whitespace-nowrap">Eventos</span>
                </Button>
                <Button
                  key="vacantes-btn"
                  variant="ghost"
                  onClick={() => navigate('/jobs')}
                  className="text-gray-300 hover:text-cyan-400 px-3 md:px-4"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  <span className="whitespace-nowrap">Vacantes</span>
                </Button>
                <Button
                  key="empresas-btn"
                  variant="ghost"
                  onClick={() => navigate('/companies')}
                  className="text-gray-300 hover:text-cyan-400 px-3 md:px-4"
                >
                  <Building className="w-4 h-4 mr-2" />
                  <span className="whitespace-nowrap">Empresas</span>
                </Button>
              </div>

              {/* Mobile dropdown (absolute) */}
              {mobileOpen && (
                <div className="md:hidden absolute left-4 right-4 top-16 z-40 bg-slate-900 border border-slate-700 rounded-lg shadow-lg">
                  <div className="flex flex-col p-2">
                    <button onClick={() => { navigate('/dashboard'); setMobileOpen(false); }} className="text-left px-4 py-2 text-gray-300 hover:bg-slate-800 hover:text-white rounded">Inicio</button>
                    <button onClick={() => { navigate('/courses'); setMobileOpen(false); }} className="text-left px-4 py-2 text-gray-300 hover:bg-slate-800 hover:text-white rounded"><BookOpen className="w-4 h-4 inline mr-2"/>Cursos</button>
                    <button onClick={() => { navigate('/events'); setMobileOpen(false); }} className="text-left px-4 py-2 text-gray-300 hover:bg-slate-800 hover:text-white rounded"><Calendar className="w-4 h-4 inline mr-2"/>Eventos</button>
                    <button onClick={() => { navigate('/jobs'); setMobileOpen(false); }} className="text-left px-4 py-2 text-gray-300 hover:bg-slate-800 hover:text-white rounded"><Briefcase className="w-4 h-4 inline mr-2"/>Vacantes</button>
                    <button onClick={() => { navigate('/companies'); setMobileOpen(false); }} className="text-left px-4 py-2 text-gray-300 hover:bg-slate-800 hover:text-white rounded"><Building className="w-4 h-4 inline mr-2"/>Empresas</button>
                  </div>
                </div>
              )}
            </div>
          </nav>

          <div className="flex items-center space-x-4">
            {user ? (
              /* Avatar with dropdown menu for authenticated users */
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(prev => !prev)}
                  aria-haspopup="true"
                  aria-expanded={!!showUserMenu}
                  className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-full px-3 py-1 hover:bg-slate-700 focus:outline-none"
                >
                  <img
                    src={user.picture || (user.email ? `https://www.gravatar.com/avatar/${encodeURIComponent(user.email)}?d=identicon` : 'https://via.placeholder.com/100?text=Sin+Foto')}
                    alt={user.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <span className="hidden md:inline text-white text-sm">Perfil</span>
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-slate-800 border border-slate-700 rounded shadow-lg z-50">
                    <div className="p-3 border-b border-slate-700">
                      <div className="flex items-center gap-3">
                        <img
                          src={user.picture || (user.email ? `https://www.gravatar.com/avatar/${encodeURIComponent(user.email)}?d=identicon` : 'https://via.placeholder.com/100?text=Sin+Foto')}
                          alt={user.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <div className="text-white text-sm line-clamp-1">{user.name}</div>
                          <div className="text-gray-400 text-xs">
                            {user.role === 'admin' ? 'Administrador' : user.role === 'empresa' ? 'Empresa' : 'Estudiante'}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col p-2">
                      {/* Solo mostrar Mi Perfil y Guardados si NO es admin */}
                      {user.role !== 'admin' && (
                        <>
                          <button onClick={handleProfileClick} className="text-left px-3 py-2 text-gray-200 hover:bg-slate-700 rounded">Mi Perfil</button>
                          <button onClick={handleSavedClick} className="text-left px-3 py-2 text-gray-200 hover:bg-slate-700 rounded">Guardados</button>
                        </>
                      )}
                      {/* Cambiar Contraseña para usuarios con cuenta por formulario */}
                      <button onClick={handleChangePasswordClick} className="text-left px-3 py-2 text-gray-200 hover:bg-slate-700 rounded">Cambiar Contraseña</button>
                      <button onClick={() => {
                        setShowUserMenu(false);
                        if (logout && typeof logout === 'function') {
                          logout();
                        } else {
                          console.error('Logout function is not available');
                        }
                      }} className="text-left px-3 py-2 text-red-400 hover:bg-slate-700 rounded">Salir</button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Login button for non-authenticated users */
              <Button
                onClick={handleLoginClick}
                className="bg-cyan-500 hover:bg-cyan-600 text-black font-semibold"
              >
                Iniciar Sesión
              </Button>
            )}
          </div>
        </div>
      </header>
    </>
  );
}
